import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { gauntlet, ApiError } from '../lib/api';
import { useSession } from '../store/session';
import { Button, Callout, Input } from '../components/ui';

/** First run: create the single admin account that owns this instance. */
export function SetupPage() {
  const signIn = useSession((s) => s.signIn);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 10;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const result = await gauntlet.setup(username, password);
      await signIn(result.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-void px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl border border-brass/25 bg-brass-wash">
            <ShieldCheck className="text-brass" size={22} />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Set up Gauntlet</h1>
          <p className="text-sm text-ink-dim">
            This creates the administrator account for this instance. It runs entirely on your own
            hardware.
          </p>
        </div>

        <form onSubmit={submit} className="panel space-y-4 p-6">
          <Input
            label="Username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            minLength={2}
            autoFocus
          />

          <Input
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={10}
            error={tooShort ? 'At least 10 characters.' : null}
            hint={!tooShort ? 'This signs you in. It does not encrypt your wallets.' : undefined}
          />

          <Input
            label="Confirm password"
            name="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            error={mismatch ? 'Passwords do not match.' : null}
          />

          {error && <Callout tone="danger">{error}</Callout>}

          <Callout tone="info" title="Two different passwords">
            This one signs you into the web interface. Each wallet you create gets its own separate
            password that encrypts its recovery phrase — so someone who gets into this server still
            cannot spend your money.
          </Callout>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            full
            loading={busy}
            disabled={!username || password.length < 10 || mismatch}
          >
            Create account
          </Button>
        </form>
      </div>
    </div>
  );
}
